import { useState } from 'react';
import { EditPencil, MoreHoriz, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useDeletePrompt } from '../hooks/use-prompts';
import type { PromptItem } from '../types';

type PromptActionDropdownProps = {
  prompt: PromptItem;
  onEdit: () => void;
  /** Folder `ownerId` when available; falls back to prompt `createdBy`. */
  ownerId?: string;
};

export function PromptActionDropdown({ prompt, onEdit, ownerId }: PromptActionDropdownProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const canEdit = useIsOwner(prompt);
  const { mutateAsync: deletePrompt, isPending } = useDeletePrompt();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDelete = async () => {
    if (!canEdit) return;

    try {
      await deletePrompt(prompt.id);
      toast({
        variant: 'success',
        title: t('TOAST_PROMPT_DELETED_TITLE') || 'Prompt deleted',
        description:
          t('TOAST_PROMPT_DELETED_DESCRIPTION', { title: prompt.title }) ||
          `${prompt.title} was deleted successfully.`,
      });
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast({
        title: t('TOAST_FAILED_TO_DELETE_PROMPT_TITLE') || 'Failed to delete prompt',
        description:
          error instanceof Error
            ? error.message
            : t('TOAST_PLEASE_TRY_AGAIN_DESCRIPTION') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };


  return (
    <>
      <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded text-grey-700 opacity-100 hover:bg-grey-50 hover:text-grey-700 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-white/70 dark:hover:bg-white/10"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <MoreHoriz width={20} height={20} strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" sideOffset={6} className="min-w-[191px]">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsMenuOpen(false);
              onEdit();
            }}
          >
            <EditPencil width={20} height={20} strokeWidth={1.5} />
            {t('EDIT_PROMPT') || 'Edit prompt'}
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem
              className={dropdownMenuItemDestructiveClass}
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
            >
              <Trash width={20} height={20} strokeWidth={1.5} />
              {t('DELETE_PROMPT') || 'Delete prompt'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canEdit ? (
        <ConfirmationModal
          open={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
          title={t('DELETE_PROMPT_CONFIRMATION_TITLE') || 'Delete prompt'}
          description={
            t('DELETE_PROMPT_CONFIRMATION_DESCRIPTION', { title: prompt.title }) ||
            `Are you sure you want to delete ${prompt.title}? This action cannot be undone.`
          }
          onConfirm={handleDelete}
          confirmText={t('DELETE') || 'Delete'}
          cancelText={t('CANCEL') || 'Cancel'}
          preventAutoClose
          isLoading={isPending}
          confirmVariant="delete"
        />
      ) : null}
    </>
  );
}
